import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import Calendar from 'core/components/DateTimePicker/base/Calendar';
import dayjs from 'utils/dayjs';
import CalendarIcon from 'core/icons/calendar';
import Button from 'pages/Dashboard/components/Button';
import DateRangeInput, {
  DateRangeInputRef,
} from 'core/components/DateTimePicker/inputs/DateRangeInput';
import isNil from 'lodash/isNil';
import Popover from 'core/components/Popover';
import {
  isEmptyString,
  notEmptyString,
} from '@populateui/utils';
import clsx from 'clsx';
import defer from 'lodash/defer';
import Presets from 'core/components/DateTimePicker/base/Presets';
import {
  PickerDateRange,
  DateRangePreset,
  PickerProps,
} from 'core/components/DateTimePicker/types';
import validatePresets from 'core/components/DateTimePicker/utils';
import {
  formatTwoDigitDate,
} from 'utils/date';
import head from 'lodash/head';
import Tooltip from 'core/components/Tooltip';

type RangeProps = PickerProps & {
  presets?: DateRangePreset[];
  defaultValue?: PickerDateRange;
  value?: PickerDateRange;
  onChange?: (dateRange: PickerDateRange | undefined) => void;
  allowEmpty?: boolean;
  autoFocusDateInput?: boolean;
};

const getDisableButtonTooltip = (
  range?: PickerDateRange,
  minDate?: Date,
  maxDate?: Date,
  allowEmpty = false,
) => {
  const result = [];
  if (allowEmpty && isNil(range?.from) && isNil(range?.to)) {
    return undefined;
  }

  if (isNil(range?.from)) {
    result.push('Please select start date');
  } else if (isNil(range?.to)) {
    result.push('Please select end date');
  } else if (dayjs(range?.from).isAfter(dayjs(range?.to))) {
    result.push('Start date must be before end date');
  } else if (!isNil(minDate) && dayjs(range?.from).isBefore(minDate)) {
    result.push(`Start date must be after ${formatTwoDigitDate(minDate)}`);
  } else if (!isNil(maxDate) && dayjs(range?.to).isAfter(maxDate)) {
    result.push(`End date must be before ${formatTwoDigitDate(maxDate)}`);
  }

  return head(result);
};

const CustomRangeDatePicker = ({
  defaultValue,
  value,
  onChange,
  presets,
  disabled,
  disableNavigation,
  disabledDays,
  enableYearNavigation = false,
  placeholder = 'Select date range',
  hasError,
  translations,
  align = 'center',
  className,
  numberOfMonths = 1,
  fromDate,
  toDate,
  allowEmpty = false,
  autoFocusDateInput = false,
  ...props
}: RangeProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);
  const [range, setRange] = useState<PickerDateRange | undefined>(
    value ?? defaultValue ?? undefined,
  );
  const [month, setMonth] = useState<Date | undefined>(range?.from);

  const initialRange = useMemo(
    () => range,
    [anchorEl],
  );

  useEffect(() => {
    setRange(value ?? defaultValue ?? undefined);
  }, [value, defaultValue]);

  useEffect(() => {
    if (range) {
      setMonth(range.from);
    }
  }, [range]);

  const dateRangeRef = useRef<DateRangeInputRef>(null);
  useEffect(() => {
    if (!open) {
      setMonth(range?.from);
    } else {
      defer(() => {
        if (autoFocusDateInput) {
          dateRangeRef.current?.focusFromInput();
        } else {
          dateRangeRef.current?.focusContainer();
        }
      });
    }
  }, [autoFocusDateInput, open]);

  const focusTo = () => {
    dateRangeRef?.current?.setFocusedTo(true);
    dateRangeRef?.current?.setFocusedFrom(false);
  };

  const updateRangeState = useCallback((newValue?: Date) => {
    const newRange = { ...range };

    if (dateRangeRef.current?.focusedFrom) {
      newRange.from = newValue;
      focusTo();
    } else if (dateRangeRef.current?.focusedTo) {
      newRange.to = dayjs(newValue).endOf('day').toDate();
    }

    if (!isNil(newRange.from) && !isNil(newRange.to) && newRange.from > newRange.to) {
      newRange.from = newValue;
      newRange.to = undefined;
      focusTo();
    }

    setRange(newRange as PickerDateRange);
  }, [range]);

  const onCancel = () => {
    setRange(initialRange);
    setAnchorEl(null);
  };

  useEffect(() => {
    setRange(value ?? defaultValue ?? undefined);
  }, [value, defaultValue]);

  const displayRange = useMemo(() => {
    if (isNil(range?.from) && isNil(range?.to)) {
      return '';
    }

    return `${range?.from ? formatTwoDigitDate(range.from) : ''} - ${
      range?.to ? formatTwoDigitDate(range.to) : ''
    }`;
  }, [range]);

  const onApply = () => {
    setAnchorEl(null);
    onChange?.(range);
  };

  const disableButtonTooltip = useMemo(
    () => getDisableButtonTooltip(range, fromDate, toDate, allowEmpty),
    [range, fromDate, toDate, allowEmpty],
  );

  return (
    <>
      <Button
        className={clsx('!min-w-[220px] !px-2 flex gap-2 !justify-start', className, {
          '!border-red-400': hasError,
        }, 'focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-default-input-focused')}
        variant="outlined"
        disabled={disabled}
        disableRipple
        onClick={(e) => {
          setAnchorEl(e.currentTarget);
        }}
        aria-required={props.required || props['aria-required']}
        aria-invalid={props['aria-invalid']}
        aria-label={props['aria-label']}
        aria-labelledby={props['aria-labelledby']}
      >
        <CalendarIcon className="!w-4 !h-4 !text-neutral-700" />
        {isEmptyString(displayRange) ? placeholder : displayRange}
      </Button>
      <Popover
        id="existing-template-menu"
        anchorEl={anchorEl}
        onClose={onCancel}
        classes={{
          paper: '!w-fit !max-w-none !overflow-visible !shadow-[0px_0px_38px_-15px_rgba(0,_0,_0,_0.35)] z-[-1]',
        }}
        anchorOrigin={{
          vertical: (anchorEl?.offsetHeight ?? 0) + 8,
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        disableEscapeKeyDown={false}
        className="max-w-3xl"
      >
        <div className="inline-flex border border-gray-100 rounded-md">
          <div className="flex flex-col overflow-x-auto sm:flex-row sm:items-start">
            {presets && presets.length > 0 && (
              <div
                className={clsx(
                  'relative flex h-16 w-full shrink-0 items-center sm:h-full sm:w-40',
                  'border-b border-gray-200 sm:border-r sm:border-b-0',
                  'overflow-auto',
                )}
              >
                <div className="absolute px-3 sm:inset-0 sm:left-0 sm:p-2">
                  <Presets
                    currentValue={range}
                    presets={presets}
                    onSelect={(value) => {
                      setRange(value);
                    }}
                  />
                </div>
              </div>
            )}
            <div className="overflow-x-auto">
              <div className="flex gap-4 justify-center items-center mt-4">
                <DateRangeInput
                  ref={dateRangeRef}
                  value={range}
                  onChange={(nextRange) => {
                    if (open) {
                      setRange(nextRange);
                    }
                  }}
                  fromLabel="From"
                  toLabel="To"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      onApply();
                    }
                  }}
                  className="date-picker-input-semibold"
                />
              </div>
              <Calendar
                mode="range"
                onDayClick={updateRangeState}
                modifiers={{
                  ...(!isNil(range?.from) && {
                    from: range?.from,
                  }),
                  ...(!isNil(range?.to) && {
                    to: range?.to,
                  }),
                  ...(range?.from && range?.to && {
                    range_middle: (date: Date) =>
                      dayjs(date).isBetween(range?.from, range?.to, 'day', '[]'),
                  }),
                }}
                modifiersClassNames={{
                  from: 'day_range_start',
                  to: 'day_range_end',
                  range_middle: 'day_range_middle',
                }}
                month={month}
                onMonthChange={setMonth}
                numberOfMonths={numberOfMonths}
                disabled={disabledDays}
                disableNavigation={disableNavigation}
                enableYearNavigation={enableYearNavigation}
                initialFocus
                classNames={{
                  months:
                    numberOfMonths > 1
                      ? 'flex gap-4 overflow-x-auto'
                      : 'flex',
                  selected: '',
                }}
                fromDate={fromDate}
                toDate={toDate}
                {...props}
              />
              <div className="border-t border-gray-200 p-3 sm:gap-2 sm:flex sm:items-center sm:justify-end">
                <Button
                  variant="outlined"
                  className="h-8 w-full sm:w-fit"
                  type="button"
                  onClick={onCancel}
                >
                  {translations?.cancel ?? 'Cancel'}
                </Button>
                <Tooltip title={disableButtonTooltip} hide={isEmptyString(disableButtonTooltip)}>
                  <span>
                    <Button
                      variant="contained"
                      color="primary"
                      className="h-8 w-full sm:w-fit"
                      type="button"
                      onClick={onApply}
                      disabled={notEmptyString(disableButtonTooltip)}
                    >
                      {translations?.apply ?? 'Apply'}
                    </Button>
                  </span>
                </Tooltip>
              </div>
            </div>
          </div>
        </div>
      </Popover>
    </>
  );
};

type RangeDatePickerProps = {
  presets?: DateRangePreset[];
  defaultValue?: PickerDateRange;
  value?: PickerDateRange;
  onChange?: (dateRange: PickerDateRange | undefined) => void;
  allowEmpty?: boolean;
  autoFocusDateInput?: boolean;
} & PickerProps;

export default function RangePicker({ presets, ...props }: RangeDatePickerProps) {
  if (presets) {
    validatePresets(presets, props);
  }

  return <CustomRangeDatePicker presets={presets} {...(props as RangeProps)} />;
}
